import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Train, CheckCircle, ArrowDown, Play, Warning } from "@phosphor-icons/react";
import { BatchPresetButtons } from "../pages/rail/shared";

/* ═══════════════════════════════════════════════════════════════════════
 * TipplerRailBridge — closed-loop link between the Grindrod Rail
 * Coordinator and the Coal Tippler operator. Every rail consignment
 * released for tipping is shown here with its full identity preserved
 * (train, mine, coal grade, client, wagon roll-up). Tippler operators
 *   Accept wagons  →  Start Consignment  →  live tippling begins.
 * Once an operation is live the "Live Operation" panel below owns the
 * +1 wagon quick-taps, pause / breakdown log and end-of-consignment.
 * ═══════════════════════════════════════════════════════════════════════ */

const CAN_ACT_ROLES = ["tippler_operator", "admin", "manager"];

function pillClass(status) {
    return {
        waiting_for_tipping:  "bg-amber-100 text-amber-800 border-amber-300",
        tipping_in_progress:  "bg-blue-100 text-blue-800 border-blue-300",
        completed:            "bg-emerald-100 text-emerald-800 border-emerald-300",
    }[status] || "bg-slate-100 text-slate-700 border-slate-300";
}

function fmtDt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-ZA", { hour12: false }); }
    catch { return iso; }
}

function ConsignmentCard({ c, canAct, isLinkedLive, liveStatus, onAction, onStart }) {
    const total = c.wagon_count || 0;
    const atTransnet = c.wagons_at_transnet || 0;
    const dispatched = c.wagons_dispatched_to_grindrod || 0;
    const atGrindrod = c.wagons_at_grindrod || 0;
    const released = c.wagons_released || 0;
    const accepted = c.wagons_accepted_at_tippler || 0;
    const tipped = c.wagons_tipped || 0;
    const pendingAtTransnet = Math.max(0, atTransnet - dispatched);
    const inFlightToGrindrod = Math.max(0, dispatched - atGrindrod);
    const pendingRelease = Math.max(0, atGrindrod - released);
    const pendingAccept = Math.max(0, released - accepted);
    const pendingTip = Math.max(0, accepted - tipped);
    const remaining = Math.max(0, total - tipped);
    const [acceptCount, setAcceptCount] = useState("");
    const [busy, setBusy] = useState(false);

    const run = async (fn) => {
        setBusy(true);
        try { await fn(); }
        finally { setBusy(false); }
    };

    const startEnabled = canAct && !isLinkedLive && (accepted > 0 || pendingAccept > 0);

    return (
        <div className={`border-2 rounded-lg bg-white shadow-sm overflow-hidden
                        ${isLinkedLive ? "border-emerald-400 shadow-emerald-100"
                                       : "border-slate-200"}`}
             data-testid={`tippler-rail-card-${c.consignment_no}`}>
            {/* Header — consignment identity preserved from mine → transnet → grindrod → tippler */}
            <div className="bg-gradient-to-r from-[#0B1A3B] to-[#1E3A8A] text-white p-3 flex flex-wrap items-center gap-3">
                <Train size={28} weight="fill" className="text-amber-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-lg font-black leading-tight">{c.consignment_no}</div>
                    <div className="text-[11px] text-amber-200 tracking-wider uppercase font-semibold">
                        {c.train_id ? `Train ${c.train_id} · ` : ""}
                        {c.mine_name || c.client_mine || c.client_name || "—"}
                        {c.coal_grade ? ` · ${c.coal_grade}` : ""}
                    </div>
                </div>
                {isLinkedLive ? (
                    <div className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"
                         data-testid={`tippler-rail-live-badge-${c.consignment_no}`}>
                        {liveStatus === "paused" ? <Warning size={12} weight="fill" /> : <Play size={12} weight="fill" />}
                        {liveStatus === "paused" ? "OP PAUSED" : "OP LIVE"}
                    </div>
                ) : (
                    <div className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider border ${pillClass(c.status)}`}>
                        {c.status.replaceAll("_", " ")}
                    </div>
                )}
            </div>

            {/* Consignment details — all preserved from Rail Coordinator */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 border-b border-slate-200 text-xs">
                <div>
                    <div className="uppercase-label text-[9px] text-slate-500">Client</div>
                    <div className="font-bold text-slate-800 truncate">{c.client_name || "—"}</div>
                </div>
                <div>
                    <div className="uppercase-label text-[9px] text-slate-500">Product</div>
                    <div className="font-bold text-slate-800">{c.product || "COAL"}</div>
                </div>
                <div>
                    <div className="uppercase-label text-[9px] text-slate-500">Tonnage</div>
                    <div className="font-bold text-slate-800">{(c.tonnage || 0).toLocaleString()} t</div>
                </div>
                <div>
                    <div className="uppercase-label text-[9px] text-slate-500">Wagons</div>
                    <div className="font-bold text-slate-800">{total}</div>
                </div>
                <div>
                    <div className="uppercase-label text-[9px] text-slate-500">Grindrod arrival</div>
                    <div className="font-mono text-[11px] text-slate-800">{fmtDt(c.actual_grindrod_arrival_at)}</div>
                </div>
            </div>

            {/* Wagon roll-up: full end-to-end pipeline */}
            <div className="p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
                <div className="rounded bg-slate-50 border border-slate-200 p-2">
                    <div className="uppercase-label text-[9px] text-slate-500">Total</div>
                    <div className="text-2xl font-black text-slate-800">{total}</div>
                </div>
                <div className="rounded bg-orange-50 border border-orange-200 p-2" title="Still at Transnet yard">
                    <div className="uppercase-label text-[9px] text-orange-700">At Transnet</div>
                    <div className="text-2xl font-black text-orange-800">{pendingAtTransnet}</div>
                    {pendingAtTransnet > 0 && (
                        <div className="text-[10px] text-orange-700 font-semibold">of {atTransnet} received</div>
                    )}
                </div>
                <div className="rounded bg-purple-50 border border-purple-200 p-2" title="In flight from Transnet to Grindrod">
                    <div className="uppercase-label text-[9px] text-purple-700">In transit</div>
                    <div className="text-2xl font-black text-purple-800">{inFlightToGrindrod}</div>
                </div>
                <div className="rounded bg-blue-50 border border-blue-200 p-2" title="At Grindrod waiting for release to Tippler">
                    <div className="uppercase-label text-[9px] text-blue-700">At Grindrod</div>
                    <div className="text-2xl font-black text-blue-800">{pendingRelease}</div>
                    {atGrindrod > 0 && (
                        <div className="text-[10px] text-blue-700 font-semibold">{atGrindrod} received</div>
                    )}
                </div>
                <div className="rounded bg-amber-50 border border-amber-200 p-2" title="Released to Tippler awaiting acceptance / tipping">
                    <div className="uppercase-label text-[9px] text-amber-700">At Tippler</div>
                    <div className="text-2xl font-black text-amber-800">{pendingAccept + pendingTip}</div>
                    <div className="text-[10px] text-amber-700 font-semibold">{released} released</div>
                </div>
                <div className="rounded bg-emerald-50 border border-emerald-200 p-2">
                    <div className="uppercase-label text-[9px] text-emerald-700">Tipped</div>
                    <div className="text-2xl font-black text-emerald-800">{tipped}</div>
                    {remaining > 0 && (
                        <div className="text-[10px] text-emerald-700 font-semibold">{remaining} to go</div>
                    )}
                </div>
            </div>

            {/* Action bar — Accept + Start (Start replaces standalone dashboard Start button) */}
            {canAct && !isLinkedLive && (
                <div className="border-t border-slate-200 bg-slate-50 p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="flex gap-2 items-center flex-wrap">
                        <div className="flex gap-2 items-center flex-1 min-w-[200px]">
                            <input
                                type="number" min="1" max={pendingAccept || undefined}
                                value={acceptCount}
                                onChange={(e) => setAcceptCount(e.target.value)}
                                placeholder={`Accept up to ${pendingAccept}`}
                                className="g-input text-sm flex-1"
                                disabled={pendingAccept <= 0 || busy}
                                data-testid={`tippler-accept-count-${c.consignment_no}`}
                            />
                            <button
                                type="button"
                                onClick={() => run(async () => {
                                    const n = parseInt(acceptCount, 10);
                                    if (!n || n <= 0) return toast.error("Enter a positive count");
                                    if (n > pendingAccept) return toast.error(`Only ${pendingAccept} pending`);
                                    await onAction(c.id, "accept_at_tippler", { count: n }, `Accepted ${n} wagons`);
                                    setAcceptCount("");
                                })}
                                disabled={pendingAccept <= 0 || busy}
                                className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-slate-900 text-xs font-black uppercase tracking-wide whitespace-nowrap"
                                data-testid={`tippler-accept-btn-${c.consignment_no}`}
                            >
                                <ArrowDown size={14} className="inline mr-1" weight="bold" />
                                Accept
                            </button>
                        </div>
                        <BatchPresetButtons max={pendingAccept}
                            onPick={(n) => setAcceptCount(String(n))}
                            testidPrefix={`tippler-accept-${c.consignment_no}`} />
                    </div>
                    <button
                        type="button"
                        onClick={() => run(async () => {
                            if (!startEnabled) return;
                            if (!window.confirm(
                                `Start tippling operation for ${c.consignment_no}? `
                                + `${pendingAccept > 0 ? `Any remaining ${pendingAccept} released wagons will be auto-accepted. ` : ""}`
                                + `The Live Operation panel below will take over for wagon counts, pauses and completion.`
                            )) return;
                            await onStart(c);
                        })}
                        disabled={!startEnabled || busy}
                        className="px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm font-black uppercase tracking-wide shadow"
                        data-testid={`tippler-start-btn-${c.consignment_no}`}
                    >
                        <Play size={16} className="inline mr-1" weight="fill" />
                        Start Consignment
                    </button>
                </div>
            )}

            {/* Complete Consignment — visible when every rail wagon is
                tipped but the rail consignment is still open (typical
                after the final batch ends). Fires rail complete_consignment
                so the coordinator can then release empties. */}
            {canAct && !isLinkedLive && tipped >= total && total > 0 && (
                <div className="border-t border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3">
                    <div className="flex-1 text-xs text-emerald-900">
                        ✓ All {total} wagons tipped — close the consignment so the
                        coordinator can release empties back to Transnet.
                    </div>
                    <button
                        type="button"
                        onClick={() => run(async () => {
                            if (!window.confirm(`Complete consignment ${c.consignment_no}? Rail Coordinator will then release empties for the return leg.`)) return;
                            await onAction(c.id, "complete_consignment", {}, `Consignment ${c.consignment_no} completed`);
                        })}
                        disabled={busy}
                        className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase tracking-widest shadow"
                        data-testid={`tippler-complete-btn-${c.consignment_no}`}
                    >
                        <CheckCircle size={14} className="inline mr-1" weight="fill" />
                        Complete Consignment
                    </button>
                </div>
            )}

            {/* Awaiting-more-wagons hint — the last batch has ended but the
                rail consignment still has wagons upstream. */}
            {canAct && !isLinkedLive && tipped > 0 && tipped < total && released === tipped && (
                <div className="border-t border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    ⏳ Batch complete — {total - tipped} wagons still en route (at
                    Transnet or in transit to Grindrod). Ask the Rail Coordinator
                    to release the next batch to the tippler when they arrive.
                </div>
            )}

                <div className="border-t border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 flex items-center gap-2"
                     data-testid={`tippler-rail-linked-${c.consignment_no}`}>
                    <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                    <span>
                        <b>Operation running below</b> — use the Live Operation panel for <b>+1 tipped</b>{" "}
                        quick-taps, <b>pause / breakdown</b> logging and <b>end consignment</b>.
                        Every tap here updates the rail wagon count end-to-end.
                    </span>
                </div>
            )}

            {c.tippler_accepted_at && (
                <div className="px-3 pb-3 text-[10px] text-slate-500">
                    First accepted at tippler {fmtDt(c.tippler_accepted_at)}
                    {c.tippler_accepted_by_name ? ` · ${c.tippler_accepted_by_name}` : ""}
                </div>
            )}
        </div>
    );
}

export default function TipplerRailBridge({ user, live, onStartOperation }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const canAct = user && CAN_ACT_ROLES.includes(user.role);
    const linkedRailId = live?.consignment?.rail_consignment_id || null;
    const liveStatus = live?.consignment?.status || null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/rail/tippler-queue");
            setRows(r.data || []);
        } catch (e) {
            if (e?.response?.status !== 403) console.warn("tippler-queue:", formatApiError(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    const doAction = async (cid, action, body, msg) => {
        try {
            await api.post(`/rail/consignments/${cid}/action`, { action, ...body });
            toast.success(msg);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const active = useMemo(() => rows.filter((r) => r.status === "tipping_in_progress"), [rows]);
    const waiting = useMemo(() => rows.filter((r) => r.status === "waiting_for_tipping"), [rows]);

    if (!rows.length && !loading) {
        return (
            <div className="g-card p-4" data-testid="tippler-rail-bridge-empty">
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Train size={20} weight="fill" className="text-slate-400" />
                    <span>No rail consignments currently released to the Coal Tippler.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="g-card p-4" data-testid="tippler-rail-bridge">
            <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex-1 min-w-0">
                    <div className="text-lg font-black text-[var(--grindrod-navy)] leading-tight">
                        Rail Consignments Released to Coal Tippler
                    </div>
                    <div className="text-[11px] text-slate-500 tracking-wide uppercase font-semibold">
                        {active.length} in progress · {waiting.length} waiting · start a consignment to begin live tippling
                    </div>
                </div>
                {!canAct && (
                    <div className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Warning size={12} weight="fill" className="text-amber-500" />
                        View-only — tippler operator must accept / start
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {rows.map((c) => (
                    <ConsignmentCard
                        key={c.id}
                        c={c}
                        canAct={canAct}
                        isLinkedLive={linkedRailId === c.id}
                        liveStatus={linkedRailId === c.id ? liveStatus : null}
                        onAction={doAction}
                        onStart={onStartOperation}
                    />
                ))}
            </div>
        </div>
    );
}
